import { useTranslation } from 'react-i18next'
import { Card } from '../ui/card'
import { IconTile } from '../ui/icon-tile'
import { Skeleton } from '../ui/skeleton'
import { cn } from '../../lib/utils'
import {
    STEERING_CATEGORIES,
    CATEGORY_VISUALS,
    categoryLabelKey,
    categoryHintKey,
    type SteeringCategory,
    type TicketCategory,
} from './steering-categories'

/**
 * The primary axis of the steering page: three cards, not a fourth dropdown.
 * Each carries a server-side count for the active status filter, and the
 * high-priority slice underneath when there is one — the card answers "how much
 * is there" and "how much of it is hot" before the user clicks.
 *
 * Its treatment is the home page's one big figure and its one lift (spec
 * 2026-09-17-05): a `Card` — so `rounded-xl shadow-card` — carrying the
 * family's gradient `IconTile` and a `text-3xl` count. The gradient is the
 * family's *identity*; everything measured stays flat.
 */
export function CategoryCards({
    active,
    counts,
    urgentCounts,
    loading,
    onSelect,
}: {
    /** The selected category, when the host has one. Home shows the same
     * three cards purely as a summary, with none of them selected. */
    active?: TicketCategory
    counts: Partial<Record<TicketCategory, number>>
    urgentCounts: Partial<Record<TicketCategory, number>>
    loading: boolean
    onSelect: (category: SteeringCategory) => void
}) {
    const { t } = useTranslation('issues')

    return (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4" data-testid="steering-categories">
            {STEERING_CATEGORIES.map((category) => {
                const { icon, gradient } = CATEGORY_VISUALS[category]
                const isActive = active === category
                const count = counts[category] ?? 0
                const urgent = urgentCounts[category] ?? 0
                return (
                    <Card
                        key={category}
                        className={cn(
                            // The one lift on the page, and a `hover:` one — so
                            // it never fires on touch — held back under
                            // `prefers-reduced-motion`.
                            'motion-safe:transition motion-safe:duration-150 hover:-translate-y-px hover:shadow-popover',
                            'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary/40',
                            isActive && 'ring-2 ring-primary/30 border-primary/40'
                        )}
                    >
                        <button
                            type="button"
                            onClick={() => onSelect(category)}
                            aria-pressed={isActive}
                            className="w-full text-left p-3 focus:outline-none"
                            data-testid={`steering-category-${category}`}
                        >
                            <div className="flex items-center gap-2">
                                <IconTile path={icon} gradient={gradient} size="md" />
                                <span className="text-sm font-medium text-foreground">{t(categoryLabelKey(category))}</span>
                            </div>
                            <div
                                className="mt-1.5 text-3xl font-semibold tabular-nums tracking-tight text-foreground"
                                data-testid={`steering-category-${category}-count`}
                            >
                                {/* The figure is the point of the tile, so its
                                    absence is drawn in the figure's shape
                                    rather than as an em dash. */}
                                {loading ? <Skeleton className="h-8 w-20" /> : count.toLocaleString()}
                            </div>
                            <div className="text-[11px] text-muted-foreground">{t(categoryHintKey(category))}</div>
                            {!loading && urgent > 0 && (
                                <div className="text-[11px] mt-1 text-warning" data-testid={`steering-category-${category}-urgent`}>
                                    {t('steering.category_urgent', { count: urgent })}
                                </div>
                            )}
                        </button>
                    </Card>
                )
            })}
        </div>
    )
}
